import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { useLanguage } from '../i18n/LanguageContext';
import { ShieldCheck, AlertTriangle, Phone, Heart, UserCheck, Car, MapPin, CheckCircle, XCircle } from 'lucide-react';

const Section = ({ icon: Icon, title, color, children }) => (
  <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
    <div className={`px-6 py-4 ${color}`}>
      <div className="flex items-center gap-3">
        <Icon className="w-6 h-6 text-white" />
        <h3 className="text-lg font-bold text-white">{title}</h3>
      </div>
    </div>
    <div className="p-6 text-gray-700 leading-relaxed space-y-3">{children}</div>
  </div>
);

const Bullet = ({ children, negative = false }) => (
  <li className="flex items-start gap-2">
    {negative ? <XCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-1" /> : <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-1" />}
    <span>{children}</span>
  </li>
);

const SafetyInformationPage = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <Navbar />
      <WhatsAppButton />

      {/* Header */}
      <section className="pt-32 pb-12 bg-gradient-to-b from-[#1D4E41] to-[#2d6a4f]">
        <div className="container mx-auto px-4 md:px-6 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white/10 backdrop-blur-sm rounded-full mb-6">
            <ShieldCheck className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">{t('safety.title')}</h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">{t('safety.subtitle')}</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          {/* Intro */}
          <div className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-6 mb-10">
            <p className="text-emerald-900 leading-relaxed">{t('safety.intro')}</p>
          </div>

          <div className="space-y-6">
            {/* General Safety */}
            <Section icon={ShieldCheck} title={t('safety.generalTitle')} color="bg-[#1D4E41]">
              <p>{t('safety.generalIntro')}</p>
              <ul className="space-y-2">
                <Bullet>{t('safety.gen1')}</Bullet>
                <Bullet>{t('safety.gen2')}</Bullet>
                <Bullet>{t('safety.gen3')}</Bullet>
                <Bullet>{t('safety.gen4')}</Bullet>
                <Bullet>{t('safety.gen5')}</Bullet>
              </ul>
            </Section>

            {/* Seat belts */}
            <Section icon={Car} title={t('safety.seatbeltsTitle')} color="bg-amber-600">
              <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg">
                <p className="font-semibold text-amber-900">{t('safety.seatbeltsHighlight')}</p>
              </div>
              <ul className="space-y-2">
                <Bullet>{t('safety.belt1')}</Bullet>
                <Bullet>{t('safety.belt2')}</Bullet>
                <Bullet>{t('safety.belt3')}</Bullet>
                <Bullet>{t('safety.belt4')}</Bullet>
                <Bullet>{t('safety.belt5')}</Bullet>
              </ul>
            </Section>

            {/* Recommendations */}
            <Section icon={UserCheck} title={t('safety.recommendationsTitle')} color="bg-blue-600">
              <h4 className="font-semibold text-blue-800">{t('safety.beforeTitle')}</h4>
              <ul className="space-y-2">
                <Bullet>{t('safety.before1')}</Bullet>
                <Bullet>{t('safety.before2')}</Bullet>
                <Bullet>{t('safety.before3')}</Bullet>
                <Bullet>{t('safety.before4')}</Bullet>
              </ul>
              <h4 className="font-semibold text-blue-800 mt-4">{t('safety.duringTitle')}</h4>
              <ul className="space-y-2">
                <Bullet>{t('safety.during1')}</Bullet>
                <Bullet>{t('safety.during2')}</Bullet>
                <Bullet>{t('safety.during3')}</Bullet>
                <Bullet>{t('safety.during4')}</Bullet>
                <Bullet>{t('safety.during5')}</Bullet>
              </ul>
              <h4 className="font-semibold text-blue-800 mt-4">{t('safety.healthTitle')}</h4>
              <ul className="space-y-2">
                <Bullet>{t('safety.health1')}</Bullet>
                <Bullet>{t('safety.health2')}</Bullet>
                <Bullet>{t('safety.health3')}</Bullet>
                <Bullet>{t('safety.health4')}</Bullet>
              </ul>
            </Section>

            {/* Conduct */}
            <Section icon={Heart} title={t('safety.conductTitle')} color="bg-purple-600">
              <p>{t('safety.conductIntro')}</p>
              <h4 className="font-semibold text-purple-800">{t('safety.doTitle')}</h4>
              <ul className="space-y-2">
                <Bullet>{t('safety.do1')}</Bullet>
                <Bullet>{t('safety.do2')}</Bullet>
                <Bullet>{t('safety.do3')}</Bullet>
                <Bullet>{t('safety.do4')}</Bullet>
              </ul>
              <h4 className="font-semibold text-purple-800 mt-4">{t('safety.dontTitle')}</h4>
              <ul className="space-y-2">
                <Bullet negative>{t('safety.dont1')}</Bullet>
                <Bullet negative>{t('safety.dont2')}</Bullet>
                <Bullet negative>{t('safety.dont3')}</Bullet>
                <Bullet negative>{t('safety.dont4')}</Bullet>
                <Bullet negative>{t('safety.dont5')}</Bullet>
              </ul>
            </Section>

            {/* Emergencies */}
            <Section icon={AlertTriangle} title={t('safety.emergencyTitle')} color="bg-red-600">
              <div className="bg-red-50 border-2 border-red-300 rounded-xl p-6 text-center">
                <p className="text-sm text-red-700 uppercase tracking-wider font-semibold mb-2">{t('safety.emergencyNumber')}</p>
                <a href="tel:911" className="text-5xl font-bold text-red-700 hover:underline">911</a>
                <p className="text-xs text-red-600 mt-2">{t('safety.emergencyNumberHint')}</p>
              </div>

              <h4 className="font-semibold text-red-800 mt-4">{t('safety.duringEmergencyTitle')}</h4>
              <ul className="space-y-2">
                <Bullet>{t('safety.emg1')}</Bullet>
                <Bullet>{t('safety.emg2')}</Bullet>
                <Bullet>{t('safety.emg3')}</Bullet>
                <Bullet>{t('safety.emg4')}</Bullet>
                <Bullet>{t('safety.emg5')}</Bullet>
              </ul>

              <h4 className="font-semibold text-red-800 mt-4">{t('safety.numbersTitle')}</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase">{t('safety.policeLabel')}</p>
                  <a href="tel:911" className="font-bold text-gray-800">911</a>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase">{t('safety.trafficLabel')}</p>
                  <a href="tel:2222-9330" className="font-bold text-gray-800">+506 2222-9330</a>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase">{t('safety.redCrossLabel')}</p>
                  <a href="tel:128" className="font-bold text-gray-800">128</a>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase">{t('safety.firefightersLabel')}</p>
                  <a href="tel:118" className="font-bold text-gray-800">118</a>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase">{t('safety.touristPoliceLabel')}</p>
                  <a href="tel:2586-4053" className="font-bold text-gray-800">+506 2586-4053</a>
                </div>
                <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200">
                  <p className="text-xs text-emerald-600 uppercase">{t('safety.tgcrLabel')}</p>
                  <a href="https://wa.me/50688173167" target="_blank" rel="noopener noreferrer" className="font-bold text-emerald-800">+506 8817-3167 (WhatsApp)</a>
                </div>
              </div>
            </Section>

            {/* Insurance & coverage */}
            <Section icon={ShieldCheck} title={t('safety.insuranceTitle')} color="bg-teal-600">
              <p>{t('safety.insuranceIntro')}</p>
              <ul className="space-y-2">
                <Bullet>{t('safety.ins1')}</Bullet>
                <Bullet>{t('safety.ins2')}</Bullet>
                <Bullet>{t('safety.ins3')}</Bullet>
              </ul>
            </Section>

            {/* Footer note */}
            <div className="bg-gradient-to-br from-emerald-500 to-[#1D4E41] rounded-2xl p-8 text-center text-white">
              <h3 className="text-2xl font-bold mb-3">{t('safety.commitmentTitle')}</h3>
              <p className="text-white/90">{t('safety.commitmentText')}</p>
              <div className="mt-4 text-2xl">🛡️ 🇨🇷</div>
            </div>
          </div>

          <p className="text-center text-gray-400 text-sm mt-10">{t('safety.lastUpdated')}</p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SafetyInformationPage;
